import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import styles from '../WeeklyBreakdownCharts.module.scss';
import { Spinner } from 'components/UI';
import type { WeeklyBreakdownReport, WeekDayKey } from 'model/BookingsWeeklyBreakdownReport';

type Slice = { name: string; value: number; percentage: number; color: string };

const COLORS = ['#BDD2CA', '#92B4A7', '#5E8677', '#BAA997', '#DCCCBB', '#EAB464', '#D5E1DB'];
const DAY_KEYS: WeekDayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const money = (n: number) =>
    (n ?? 0).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

interface Props {
    loading: boolean;
    report: WeeklyBreakdownReport;
}

export const NetSalesChart: React.FC<Props> = ({ loading, report }) => {
    const data: Slice[] = React.useMemo(() => {
        if (!report?.netSales) return [];

        return DAY_KEYS.map((k, i) => {
            const dayLabel = report.days?.[k]?.format('ddd, [[]MM/DD/YYYY[]]') ?? `${k}`;
            const value = report.netSales[k] ?? 0;
            const pct = report.salesPct?.[k] ?? 0;

            return {
                name: dayLabel,
                value,
                percentage: Number(pct),
                color: COLORS[i % COLORS.length]
            };
        });
    }, [report]);

    const total = report?.netSales?.total ?? 0;

    return (
        <div className={styles.card}>
            <h3 className={styles.title}>Net Sales</h3>

            <div className={styles.content}>
                {loading ? (
                    <div className={styles.loading}>
                        <Spinner className={styles.spinner} />
                    </div>
                ) : (
                    <>
                        <div className={styles.pieWrap}>
                            <div style={{ width: 186, height: 186 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={data}
                                            dataKey="value"
                                            cx="50%"
                                            cy="50%"
                                            innerRadius="60%"
                                            outerRadius="80%"
                                            startAngle={90}
                                            endAngle={450}
                                            stroke="none"
                                        >
                                            {data.map((s, i) => (
                                                <Cell
                                                    key={i}
                                                    fill={s.color}
                                                />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            <div className={styles.centerLabel}>
                                <div className={styles.totalValue}>{money(total)}</div>
                                <div className={styles.totalPercentage}>100%</div>
                            </div>
                        </div>

                        <div className={styles.legend}>
                            {data.map((s, i) => (
                                <div
                                    key={i}
                                    className={styles.legendItem}
                                >
                                    <div
                                        className={styles.legendDot}
                                        style={{ backgroundColor: s.color }}
                                    />
                                    <span className={styles.legendLabel}>{s.name}</span>
                                    <span className={styles.legendValue}>
                                        {money(s.value)} ({s.percentage}%)
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default NetSalesChart;
